/**
 * Yasmin Jay — native cart drawer
 * Fast path: optimistic header count + open drawer; refresh from /cart.js without blocking.
 */
(function () {
  'use strict';

  var FREE_SHIP_THRESHOLD = 30000;
  // Lines are rendered by the section (Liquid) via the Section Rendering API, so they can
  // show the sale "was" price, which /cart.js doesn't include.
  var SECTION_ID = 'cart-drawer';
  var drawer;
  var panel;
  var linesEl;
  var emptyEl;
  var footerEl;
  var titleCountEl;
  var shippingEl;
  var shippingMsgEl;
  var bodyEl;
  var checkoutTotalEl;
  var checkoutBtn;
  var noticeEl;
  var showShipping = false;
  var moneyFormat = null;
  var cartChangeUrl = '/cart/change';
  var cartUrl = '/cart';
  var requestSeq = 0;
  var isOpen = false;
  var pendingFocus = null;
  var noticeTimer = null;

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /** Plain-text money only — shop money_format often includes <span class=money> HTML */
  function formatMoney(cents) {
    var raw;
    // Force a non-HTML format so we never leak tags into textContent
    var plainFormat = '${{amount}}';
    if (window.theme && theme.Shopify && typeof theme.Shopify.formatMoney === 'function') {
      raw = theme.Shopify.formatMoney(cents, plainFormat);
    } else {
      raw = '$' + (Number(cents) / 100).toFixed(2);
    }
    return String(raw).replace(/<[^>]*>/g, '').trim();
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function updateHeaderCount(count) {
    var summaries = qsa('#pageheader .cart-summary, .logo-area .cart-summary');
    summaries.forEach(function (summary) {
      var link = qs('.cart-link', summary) || summary;
      var icon = qs('.cart-link__icon', link) || link;
      var countEl = qs('.cart-link__count', link);

      if (count > 0) {
        if (!countEl) {
          countEl = document.createElement('span');
          countEl.className = 'cart-link__count';
          icon.appendChild(countEl);
        }
        if (String(countEl.textContent) !== String(count)) {
          countEl.textContent = String(count);
        }
      } else if (countEl) {
        countEl.remove();
      }
    });
  }

  function setTitleCount(count) {
    if (!titleCountEl) return;
    titleCountEl.textContent = count > 0 ? ' • ' + count : '';
  }

  function updateShipping(cart) {
    if (!shippingEl) return;
    if (!showShipping) {
      shippingEl.hidden = true;
      return;
    }
    shippingEl.hidden = false;
    var total = cart.total_price || 0;
    var remaining = Math.max(FREE_SHIP_THRESHOLD - total, 0);
    var pct = FREE_SHIP_THRESHOLD > 0 ? Math.min(100, (total / FREE_SHIP_THRESHOLD) * 100) : 0;
    if ((cart.item_count || 0) === 0) {
      remaining = FREE_SHIP_THRESHOLD;
      pct = 0;
    }
    var complete = remaining === 0 && (cart.item_count || 0) > 0;

    shippingEl.classList.toggle('is-complete', complete);

    if (complete) {
      shippingMsgEl.textContent = 'Free shipping unlocked!';
    } else {
      shippingMsgEl.innerHTML =
        "You're <strong>" + escapeHtml(formatMoney(remaining)) + '</strong> away from free shipping!';
    }

    // One value drives the fill and both labels (see cart-drawer.css)
    shippingEl.style.setProperty('--yj-ship-pct', pct.toFixed(2) + '%');
  }

  function linesFromSection(html) {
    if (!html || typeof DOMParser === 'undefined') return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var lines = doc.querySelector('[data-yj-cart-lines]');
    return lines ? lines.innerHTML : null;
  }

  function sectionsUrl() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + '?sections=' + SECTION_ID;
  }

  function renderCart(cart, sectionHtml) {
    var count = cart.item_count || 0;

    updateHeaderCount(count);
    setTitleCount(count);
    updateShipping(cart);

    if (checkoutTotalEl) checkoutTotalEl.textContent = formatMoney(cart.total_price || 0);
    if (footerEl) footerEl.hidden = count === 0;

    if (bodyEl) bodyEl.classList.toggle('is-empty', count === 0);
    if (emptyEl) emptyEl.hidden = count > 0;
    if (linesEl) {
      linesEl.hidden = count === 0;
      var sectionLines = linesFromSection(sectionHtml);
      // If the lines didn't come back, keep the ones showing; totals above are still current.
      if (sectionLines !== null) linesEl.innerHTML = sectionLines;
    }
  }

  function fetchCart() {
    var seq = ++requestSeq;
    var cartRequest = fetch(cartUrl + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      return r.json();
    });
    var sectionRequest = fetch(sectionsUrl(), { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
    return Promise.all([cartRequest, sectionRequest]).then(function (res) {
      var cart = res[0];
      if (seq !== requestSeq) return cart; // stale
      renderCart(cart, res[1] && res[1][SECTION_ID]);
      return cart;
    });
  }

  function openDrawer() {
    if (!drawer) return;
    if (drawer._yjCloseTimer) {
      window.clearTimeout(drawer._yjCloseTimer);
      drawer._yjCloseTimer = null;
    }
    drawer.removeAttribute('hidden');
    pendingFocus = document.activeElement;
    // Double rAF so the closed transform paints before opening
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('yj-cart-open');
        isOpen = true;
        if (panel) {
          try {
            panel.focus({ preventScroll: true });
          } catch (e) {}
        }
      });
    });
  }

  function closeDrawer() {
    if (!drawer || !isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('yj-cart-open');

    var finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      if (panel) panel.removeEventListener('transitionend', onEnd);
      if (!isOpen) drawer.setAttribute('hidden', '');
      drawer._yjCloseTimer = null;
    }

    function onEnd(e) {
      if (e.target !== panel || e.propertyName !== 'transform') return;
      finish();
    }

    if (panel) panel.addEventListener('transitionend', onEnd);
    drawer._yjCloseTimer = window.setTimeout(finish, 420);

    if (pendingFocus && typeof pendingFocus.focus === 'function') {
      try {
        pendingFocus.focus({ preventScroll: true });
      } catch (e) {}
    }
  }

  function friendlyCartError(data) {
    var raw = '';
    if (data) {
      if (typeof data.description === 'string' && data.description) raw = data.description;
      else if (typeof data.message === 'string' && data.message && data.message !== 'Cart Error') raw = data.message;
      else if (typeof data.errors === 'string') raw = data.errors;
      else if (data.errors && typeof data.errors === 'object') {
        try {
          raw = Object.keys(data.errors)
            .map(function (k) {
              var v = data.errors[k];
              return Array.isArray(v) ? v.join(' ') : String(v);
            })
            .join(' ');
        } catch (e) {
          raw = '';
        }
      }
    }
    raw = String(raw || '').trim();
    if (/can'?t add more|maximum|limit|only\s+\d+|purchase limit|one per/i.test(raw)) {
      return 'This item has a purchase limit, so the quantity couldn’t be increased.';
    }
    if (raw && raw !== 'Cart Error' && raw !== 'cart error') return raw;
    return 'That quantity isn’t available for this item.';
  }

  function isCartPayload(data) {
    return !!(data && Array.isArray(data.items));
  }

  function showNotice(message) {
    if (!noticeEl || !message) return;
    noticeEl.textContent = message;
    noticeEl.hidden = false;
    noticeEl.classList.remove('is-leaving');
    noticeEl.classList.add('is-visible');
    if (noticeTimer) window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(function () {
      hideNotice();
    }, 5200);
  }

  function hideNotice() {
    if (!noticeEl) return;
    if (noticeTimer) {
      window.clearTimeout(noticeTimer);
      noticeTimer = null;
    }
    if (noticeEl.hidden) return;
    noticeEl.classList.add('is-leaving');
    noticeEl.classList.remove('is-visible');
    window.setTimeout(function () {
      if (!noticeEl) return;
      noticeEl.hidden = true;
      noticeEl.classList.remove('is-leaving');
      noticeEl.textContent = '';
    }, 220);
  }

  function changeLine(key, quantity) {
    if (!drawer) return Promise.resolve();

    applyOptimisticChange(key, quantity);

    var seq = ++requestSeq;
    var body = {
      id: key,
      quantity: quantity,
      sections: SECTION_ID
    };
    return fetch(cartChangeUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r
          .json()
          .catch(function () {
            return null;
          })
          .then(function (data) {
            return { ok: r.ok, status: r.status, data: data };
          });
      })
      .then(function (res) {
        if (seq !== requestSeq) return res && res.data;
        var data = res && res.data;

        if (!res.ok || !isCartPayload(data)) {
          showNotice(friendlyCartError(data));
          return fetchCart();
        }

        var matched = null;
        for (var i = 0; i < data.items.length; i++) {
          if (data.items[i].key === key) {
            matched = data.items[i];
            break;
          }
        }
        if (quantity > 0 && matched && matched.quantity < quantity) {
          showNotice('This item has a purchase limit, so the quantity couldn’t be increased.');
        } else if (quantity > 0 && !matched) {
          showNotice(friendlyCartError(data));
        } else {
          hideNotice();
        }

        renderCart(data, data.sections && data.sections[SECTION_ID]);
        return data;
      })
      .catch(function () {
        if (seq !== requestSeq) return;
        showNotice('Something went wrong updating your cart. Please try again.');
        return fetchCart();
      });
  }

  function applyOptimisticChange(key, quantity) {
    if (!linesEl) return;
    var line = null;
    qsa('.yj-cart-drawer__line', linesEl).forEach(function (el) {
      if (el.getAttribute('data-key') === key) line = el;
    });
    if (!line) return;

    var qtyEl = qs('[data-yj-qty-value]', line);
    var nowEl = qs('[data-yj-line-now]', line);
    var wasEl = qs('[data-yj-line-was]', line);
    var unitPrice = parseInt(line.getAttribute('data-unit-price'), 10);
    if (isNaN(unitPrice)) unitPrice = 0;
    var unitWas = parseInt(line.getAttribute('data-unit-was'), 10) || 0;

    if (quantity <= 0) {
      line.remove();
    } else {
      line.setAttribute('data-qty', String(quantity));
      if (qtyEl) qtyEl.textContent = String(quantity);
      if (nowEl) nowEl.textContent = formatMoney(unitPrice * quantity);
      if (wasEl) wasEl.textContent = formatMoney(unitWas * quantity);
    }

    var count = 0;
    var total = 0;
    qsa('.yj-cart-drawer__line', linesEl).forEach(function (el) {
      var q = parseInt(el.getAttribute('data-qty'), 10) || 0;
      var u = parseInt(el.getAttribute('data-unit-price'), 10) || 0;
      count += q;
      total += u * q;
    });

    updateHeaderCount(count);
    setTitleCount(count);
    if (checkoutTotalEl) checkoutTotalEl.textContent = formatMoney(total);
    if (footerEl) footerEl.hidden = count === 0;
    if (bodyEl) bodyEl.classList.toggle('is-empty', count === 0);
    if (emptyEl) emptyEl.hidden = count > 0;
    if (linesEl) linesEl.hidden = count === 0;

    updateShipping({ total_price: total, item_count: count });
  }

  function onItemAdded(itemData, addedQty) {
    var bump = parseInt(addedQty, 10);
    if (!bump || bump < 1) bump = 1;

    var currentCountEl = qs('#pageheader .cart-link__count, .logo-area .cart-link__count');
    var current = currentCountEl ? parseInt(currentCountEl.textContent, 10) || 0 : 0;
    updateHeaderCount(current + bump);
    setTitleCount(current + bump);

    openDrawer();
    fetchCart().catch(function () {});
  }

  function bindAjaxAddToCart() {
    if (!window.jQuery || !window.theme) return;

    theme.applyAjaxToProductForm = function ($form) {
      if (theme.settings.cart_type !== 'drawer') return;

      $form.filter('[data-ajax-add-to-cart="true"]').each(function () {
        var $el = jQuery(this);
        $el.off('submit.yjCart submit');
        $el.on('submit.yjCart', function (evt) {
          evt.preventDefault();
          var $f = jQuery(this);
          var $btns = $f.find('[type="submit"], button[name="add"]');
          var addedQty = parseInt($f.find('[name="quantity"]').val(), 10) || 1;

          $btns.prop('disabled', true).each(function () {
            var $b = jQuery(this);
            if ($b.is('input')) {
              $b.data('previous-value', $b.val());
              $b.val(theme.strings.products_product_adding_to_cart);
            } else {
              if (!$b.data('previous-html')) $b.data('previous-html', $b.html());
              $b.text(theme.strings.products_product_adding_to_cart);
            }
          });

          jQuery
            .post(theme.routes.cart_add_url + '.js', $f.serialize(), null, 'json')
            .done(function (itemData) {
              $btns.prop('disabled', false).each(function () {
                var $b = jQuery(this);
                if ($b.is('input')) {
                  $b.val(theme.strings.products_product_added_to_cart).addClass('inverted');
                  window.setTimeout(function () {
                    $b.removeClass('inverted').val($b.data('previous-value'));
                  }, 2200);
                } else {
                  $b.text(theme.strings.products_product_added_to_cart);
                  window.setTimeout(function () {
                    $b.html($b.data('previous-html'));
                  }, 2200);
                }
              });

              onItemAdded(itemData, addedQty);

              // Close Symmetry quick-buy if open
              jQuery('.quickbuy-container .close, .quickbuy-close').first().trigger('click');
            })
            .fail(function (xhr) {
              $btns.prop('disabled', false).each(function () {
                var $b = jQuery(this);
                if ($b.is('input')) {
                  $b.val($b.data('previous-value'));
                } else if ($b.data('previous-html')) {
                  $b.html($b.data('previous-html'));
                }
              });
              if (xhr && xhr.responseJSON && xhr.responseJSON.description) {
                theme.showQuickPopup(xhr.responseJSON.description, $f.find('[type="submit"]:first'));
              } else if (xhr && xhr.responseText) {
                try {
                  var jsonRes = JSON.parse(xhr.responseText);
                  theme.showQuickPopup(jsonRes.description, $f.find('[type="submit"]:first'));
                } catch (err) {
                  $f.off('submit.yjCart');
                  $f.get(0).submit();
                }
              } else {
                $f.off('submit.yjCart');
                $f.get(0).submit();
              }
            });
        });
      });
    };

    // Kill Symmetry overlay scroll dismiss handler noise
    if (window.jQuery) jQuery(window).off('.ajaxAddScroll');

    // Rebind existing forms now
    jQuery('form[action*="/cart/add"][data-ajax-add-to-cart="true"]').each(function () {
      jQuery(this).off('submit');
      theme.applyAjaxToProductForm(jQuery(this));
    });
  }

  function onDocumentClick(e) {
    var t = e.target;

    // Header cart → open drawer
    var cartLink = t.closest && t.closest('#pageheader .cart-link, .logo-area .cart-link');
    if (cartLink) {
      e.preventDefault();
      openDrawer();
      fetchCart().catch(function () {});
      return;
    }

    if (!drawer) return;

    if (t.closest('[data-yj-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    var removeBtn = t.closest('[data-yj-remove]');
    if (removeBtn && drawer.contains(removeBtn)) {
      e.preventDefault();
      changeLine(removeBtn.getAttribute('data-key'), 0);
      return;
    }

    var dec = t.closest('[data-yj-qty-dec]');
    if (dec && drawer.contains(dec)) {
      e.preventDefault();
      var lineDec = dec.closest('.yj-cart-drawer__line');
      var valDec = qs('[data-yj-qty-value]', lineDec);
      var nextDec = Math.max(0, (parseInt(valDec && valDec.textContent, 10) || 0) - 1);
      changeLine(dec.getAttribute('data-key'), nextDec);
      return;
    }

    var inc = t.closest('[data-yj-qty-inc]');
    if (inc && drawer.contains(inc)) {
      e.preventDefault();
      var lineInc = inc.closest('.yj-cart-drawer__line');
      var valInc = qs('[data-yj-qty-value]', lineInc);
      var nextInc = (parseInt(valInc && valInc.textContent, 10) || 0) + 1;
      changeLine(inc.getAttribute('data-key'), nextInc);
      return;
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      closeDrawer();
    }
  }

  // Catch non-theme ATC (SellUp / Vex / fetch) without fighting the theme jQuery path
  function installFetchHook() {
    if (!window.fetch || window.__yjCartFetchHooked) return;
    window.__yjCartFetchHooked = true;
    var originalFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var input = args[0];
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartAdd = url && url.indexOf('/cart/add') !== -1;
      return originalFetch.apply(this, args).then(function (response) {
        try {
          if (response.ok && isCartAdd && !isOpen) {
            // App-driven adds (not theme jQuery) — open + refresh
            openDrawer();
            fetchCart().catch(function () {});
          }
        } catch (err) {}
        return response;
      });
    };
  }

  function init() {
    drawer = qs('[data-yj-cart-drawer]');
    if (!drawer) return;

    panel = qs('[data-yj-cart-panel]', drawer);
    linesEl = qs('[data-yj-cart-lines]', drawer);
    emptyEl = qs('[data-yj-cart-empty]', drawer);
    footerEl = qs('[data-yj-cart-footer]', drawer);
    bodyEl = qs('[data-yj-cart-body]', drawer);
    titleCountEl = qs('[data-yj-cart-title-count]', drawer);
    shippingEl = qs('[data-yj-cart-shipping]', drawer);
    shippingMsgEl = qs('[data-yj-cart-shipping-msg]', drawer);
    checkoutTotalEl = qs('[data-yj-cart-total]', drawer);
    checkoutBtn = qs('[data-yj-checkout]', drawer);
    noticeEl = qs('[data-yj-cart-notice]', drawer);

    if (shippingEl) {
      FREE_SHIP_THRESHOLD = parseInt(shippingEl.getAttribute('data-threshold'), 10) || 30000;
      showShipping = shippingEl.getAttribute('data-show-shipping') === 'true';
    }
    moneyFormat = '${{amount}}';
    cartChangeUrl = drawer.getAttribute('data-cart-change-url') || '/cart/change';
    cartUrl = drawer.getAttribute('data-cart-url') || '/cart';

    if (checkoutBtn) {
      checkoutBtn.setAttribute('href', '/checkout');
    }

    // Normalize any Liquid money HTML left in the DOM to plain text
    if (checkoutTotalEl) {
      checkoutTotalEl.textContent = (checkoutTotalEl.textContent || '').replace(/<[^>]*>/g, '').trim() || checkoutTotalEl.textContent;
      // If Liquid rendered nested .money span, flatten:
      checkoutTotalEl.textContent = checkoutTotalEl.textContent.trim();
    }
    qsa('[data-yj-line-now], [data-yj-line-was]', drawer).forEach(function (el) {
      el.textContent = el.textContent.trim();
    });

    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', onKeydown, true);

    bindAjaxAddToCart();
    window.setTimeout(bindAjaxAddToCart, 50);
    window.setTimeout(bindAjaxAddToCart, 400);

    installFetchHook();

    // Sync shipping copy immediately (fixes Liquid money spans in msg)
    fetchCart().catch(function () {});

    window.YJCart = {
      open: function () {
        openDrawer();
        return fetchCart();
      },
      close: closeDrawer,
      refresh: fetchCart,
      onItemAdded: onItemAdded
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
